import { useMemo } from "react";
import { Text, useGLTF } from "@react-three/drei";
import {
  BallCollider,
  CuboidCollider,
  CylinderCollider,
  RigidBody,
} from "@react-three/rapier";
import * as THREE from "three";

export default function RigidObjects() {
  const beachBallGeometry = useMemo(() => {
    const geometry = new THREE.SphereGeometry(0.5, 32, 32);
    const palette = ["#008DF6", "#4DE284", "#F8C52C", "#F06968"].map(
      (hex) => new THREE.Color(hex)
    );

    const positionAttr = geometry.getAttribute("position");
    const colors = new Float32Array(positionAttr.count * 3);
    const color = new THREE.Color();
    const fullTurn = Math.PI * 2;
    const segmentSpan = fullTurn / palette.length;

    for (let i = 0; i < positionAttr.count; i += 1) {
      const x = positionAttr.getX(i);
      const z = positionAttr.getZ(i);
      let angle = Math.atan2(z, x);
      if (angle < 0) angle += fullTurn;

      const segmentIndex = Math.floor(angle / segmentSpan) % palette.length;
      color.copy(palette[segmentIndex]);

      colors[i * 3 + 0] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return geometry;
  }, []);

  return (
    <>
      {/* Rigid body boxes */}
      <RigidBody position={[15, 1, 2]}>
        <mesh receiveShadow castShadow>
          <boxGeometry args={[0.5, 0.5, 0.5]} />
          <meshStandardMaterial color={"lightsteelblue"} />
        </mesh>
      </RigidBody>
      <RigidBody position={[15.1, 0, 2]}>
        <mesh receiveShadow castShadow>
          <boxGeometry args={[0.5, 0.5, 0.5]} />
          <meshStandardMaterial color={"lightsteelblue"} />
        </mesh>
      </RigidBody>
      <RigidBody position={[15, 0, 0]} colliders={false}>
        <CuboidCollider args={[0.5, 0.5, 0.5]} />
        <mesh receiveShadow castShadow>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color={"lightsteelblue"} />
        </mesh>
      </RigidBody>
      <RigidBody position={[15, 0, -2]} colliders={false}>
        <CuboidCollider args={[1.5 / 2, 1.5 / 2, 1.5 / 2]} />
        <mesh receiveShadow castShadow>
          <boxGeometry args={[1.5, 1.5, 1.5]} />
          <meshStandardMaterial color={"lightsteelblue"} />
        </mesh>
      </RigidBody>
      <RigidBody position={[15, 0, -5]} colliders={false}>
        <CuboidCollider args={[1, 1, 1]} />
        <mesh receiveShadow castShadow>
          <boxGeometry args={[2, 2, 2]} />
          <meshStandardMaterial color={"lightsteelblue"} />
        </mesh>
      </RigidBody>

      {/* Dice */}
      <DiceRigidBody />


      {/* Fun toy */}
      <RigidBody colliders={false} position={[26, 0, -15]}>
        <CylinderCollider args={[0.03, 2.5]} position={[0, 0.25, 0]} />
        <BallCollider args={[0.25]} />
        <mesh receiveShadow castShadow>
          <cylinderGeometry args={[2.5, 0.2, 0.5]} />
          <meshStandardMaterial color={"#F8C52C"} />
        </mesh>
      </RigidBody>
      {/* Beach ball */}
      <RigidBody colliders={false} position={[49.05, 1.3, -11.82]} scale={2.5} mass={0.5}>
        <BallCollider args={[0.5]} />
        <mesh receiveShadow castShadow geometry={beachBallGeometry}>
          <meshStandardMaterial vertexColors />
        </mesh>
      </RigidBody>
    </>
  );
}

function DiceRigidBody() {
  const { scene } = useGLTF("/dice.glb");
  const diceScene = useMemo(() => {
    if (!scene) return null;
    const clone = scene.clone(true);
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3();
    box.getSize(size);
    const largestAxis = Math.max(size.x, size.y, size.z);
    if (Number.isFinite(largestAxis) && largestAxis > 0) {
      const targetSize = 1.5;
      const scaleFactor = targetSize / largestAxis;
      clone.scale.setScalar(scaleFactor);
    }
    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    return clone;
  }, [scene]);

  return (
    <RigidBody position={[-13.83, 0, -13.29]} colliders={false}>
      <CuboidCollider args={[1.5 / 2, 1.5 / 2, 1.5 / 2]} />
      {diceScene && <primitive object={diceScene} />}
    </RigidBody>
  );
}

useGLTF.preload("/dice.glb");
